/**
 * Stratus Complete System pre-cart upsell
 *
 * Qualifying products:
 * - Performance Facial Cleanser
 * - Performance Facial Moisturizer
 *
 * The script intercepts Alchemy's product-form submission before the
 * single product is added. The customer can then either:
 *
 * 1. Add the matching Complete System purchase option, or
 * 2. Continue with the original single product.
 */

(() => {
  'use strict';

  /* =========================================================
     STORE CONFIGURATION
     ========================================================= */

  const CONFIG = {
    variants: {
      cleanser: '52986829537585',
      moisturizer: '52986829930801',
      completeSystem: '53830358106417'
    },

    sellingPlans: {
      /* Single-product Joy selling plans */
      singles: {
        '692326596913': {
          frequency: 'Every 30 days',
          bundlePlan: '692326629681'
        },
        '692265812273': {
          frequency: 'Every 45 days',
          bundlePlan: '692324106545'
        },
        '692265845041': {
          frequency: 'Every 60 days',
          bundlePlan: '692324139313'
        }
      }
    },

    bundleContents: 'Performance Facial Cleanser x1, Performance Facial Moisturizer x1',

    storage: {
      accepted: 'stratus_bundle_upsell_accepted',
      shownThisSession: 'stratus_bundle_upsell_shown_session',
      declinedUntil: 'stratus_bundle_upsell_declined_until'
    },

    declineCooldownMinutes: 30,

    copy: {
      heading: 'Complete Your Routine',
      oneTimeBody:
        'Add the matching product for just $16 more. You\'ll save $8 compared to buying each product separately.',
      subscriptionBody:
        'Add the matching product for just $14 more. You\'ll save $7 compared to buying each product separately.',
      oneTimeButton: 'Upgrade and Save $8',
      subscriptionButton: 'Upgrade and Save $7',
      declineButton: 'No thanks, continue with one product',
      loadingButton: 'Upgrading your cart...'
    }
  };

  /* =========================================================
     INTERNAL STATE
     ========================================================= */

  const bypassedForms = new WeakSet();

  let pendingForm = null;
  let pendingSubmitter = null;
  let pendingQuantity = 1;
  let pendingSellingPlan = '';
  let previouslyFocusedElement = null;
  let isProcessing = false;

  /* =========================================================
     STORAGE HELPERS
     ========================================================= */

  function storageIsAvailable(type) {
    try {
      const storage = window[type];
      const testKey = '__stratus_storage_test__';

      storage.setItem(testKey, testKey);
      storage.removeItem(testKey);

      return true;
    } catch (error) {
      return false;
    }
  }

  const hasLocalStorage = storageIsAvailable('localStorage');
  const hasSessionStorage = storageIsAvailable('sessionStorage');

  function hasAcceptedOffer() {
    return hasLocalStorage
      && window.localStorage.getItem(CONFIG.storage.accepted) === 'true';
  }

  function markOfferAccepted() {
    if (hasLocalStorage) {
      window.localStorage.setItem(CONFIG.storage.accepted, 'true');
    }

    if (hasSessionStorage) {
      window.sessionStorage.setItem(CONFIG.storage.shownThisSession, 'true');
    }
  }

  function hasShownThisSession() {
    return hasSessionStorage
      && window.sessionStorage.getItem(CONFIG.storage.shownThisSession) === 'true';
  }

  function markShownThisSession() {
    if (hasSessionStorage) {
      window.sessionStorage.setItem(CONFIG.storage.shownThisSession, 'true');
    }
  }

  function isInDeclineCooldown() {
    if (!hasLocalStorage) return false;

    const declinedUntil = Number(
      window.localStorage.getItem(CONFIG.storage.declinedUntil)
    );

    return Boolean(declinedUntil && Date.now() < declinedUntil);
  }

  function startDeclineCooldown() {
    if (!hasLocalStorage) return;

    const cooldownMilliseconds =
      CONFIG.declineCooldownMinutes * 60 * 1000;

    window.localStorage.setItem(
      CONFIG.storage.declinedUntil,
      String(Date.now() + cooldownMilliseconds)
    );
  }

  /* =========================================================
     MODAL CREATION
     ========================================================= */

  function createModal() {
    if (document.getElementById('stratus-bundle-upsell')) return;

    const modal = document.createElement('div');

    modal.id = 'stratus-bundle-upsell';
    modal.className = 'stratus-upsell';
    modal.hidden = true;

    modal.innerHTML = `
      <div
        class="stratus-upsell__overlay"
        data-stratus-upsell-dismiss
      ></div>

      <div
        class="stratus-upsell__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="stratus-upsell-heading"
        aria-describedby="stratus-upsell-description"
        tabindex="-1"
      >
        <button
          type="button"
          class="stratus-upsell__close"
          aria-label="Close"
          data-stratus-upsell-dismiss
        >
          <svg
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            aria-hidden="true"
          >
            <path d="M5 5L19 19M5 19L19 5"></path>
          </svg>
        </button>

        <div class="stratus-upsell__eyebrow">
          STRATUS COMPLETE SYSTEM
        </div>

        <h2
          class="h3 stratus-upsell__heading"
          id="stratus-upsell-heading"
        >
          ${CONFIG.copy.heading}
        </h2>

        <p
          class="stratus-upsell__description"
          id="stratus-upsell-description"
        ></p>

        <div class="stratus-upsell__products">
          <div class="stratus-upsell__product">
            <span class="stratus-upsell__check" aria-hidden="true">✓</span>
            <span>Performance Facial Cleanser</span>
          </div>

          <div class="stratus-upsell__product">
            <span class="stratus-upsell__check" aria-hidden="true">✓</span>
            <span>Performance Facial Moisturizer</span>
          </div>
        </div>

        <p
          class="stratus-upsell__frequency"
          id="stratus-upsell-frequency"
          hidden
        ></p>

        <div
          class="alert mt-4 bg-error-bg text-error-text stratus-upsell__error"
          role="alert"
          hidden
        ></div>

        <button
          type="button"
          class="btn btn--primary w-full stratus-upsell__accept"
        ></button>

        <button
          type="button"
          class="stratus-upsell__decline"
          data-stratus-upsell-dismiss
        >
          ${CONFIG.copy.declineButton}
        </button>
      </div>
    `;

    document.body.appendChild(modal);

    injectStyles();

    modal
      .querySelector('.stratus-upsell__accept')
      .addEventListener('click', acceptUpsell);

    modal
      .querySelectorAll('[data-stratus-upsell-dismiss]')
      .forEach((element) => {
        element.addEventListener('click', declineUpsell);
      });

    document.addEventListener('keydown', handleModalKeydown);
  }

  function injectStyles() {
    if (document.getElementById('stratus-bundle-upsell-styles')) return;

    const style = document.createElement('style');

    style.id = 'stratus-bundle-upsell-styles';

    style.textContent = `
      .stratus-upsell[hidden] {
        display: none !important;
      }

      .stratus-upsell {
        position: fixed;
        inset: 0;
        z-index: 100000;
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 20px;
      }

      .stratus-upsell__overlay {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.72);
        backdrop-filter: blur(3px);
        -webkit-backdrop-filter: blur(3px);
        animation: stratus-upsell-fade-in 180ms ease;
      }

      .stratus-upsell__dialog {
        position: relative;
        z-index: 1;
        width: min(100%, 520px);
        max-height: calc(100vh - 40px);
        overflow-y: auto;
        padding: 38px 34px 30px;
        background: rgb(var(--bg-color, 255 255 255));
        color: rgb(var(--text-color, 0 0 0));
        border-radius: var(--btn-border-radius, 4px);
        box-shadow: 0 24px 70px rgba(0, 0, 0, 0.3);
        animation: stratus-upsell-slide-in 220ms ease;
      }

      .stratus-upsell__close {
        position: absolute;
        top: 14px;
        right: 14px;
        display: flex;
        align-items: center;
        justify-content: center;
        width: 38px;
        height: 38px;
        padding: 0;
        color: currentColor;
        background: transparent;
        border: 0;
        cursor: pointer;
      }

      .stratus-upsell__eyebrow {
        margin-bottom: 10px;
        padding-right: 30px;
        font-size: 0.95rem;
        font-weight: 700;
        line-height: 1.3;
        letter-spacing: 0.12em;
        opacity: 0.68;
      }

      .stratus-upsell__heading {
        margin: 0 0 14px;
      }

      .stratus-upsell__description {
        margin: 0 0 22px;
        line-height: 1.55;
      }

      .stratus-upsell__products {
        margin-bottom: 18px;
        padding: 18px;
        background: rgba(127, 127, 127, 0.09);
        border-radius: var(--btn-border-radius, 4px);
      }

      .stratus-upsell__product {
        display: flex;
        align-items: flex-start;
        gap: 10px;
        line-height: 1.4;
      }

      .stratus-upsell__product + .stratus-upsell__product {
        margin-top: 11px;
      }

      .stratus-upsell__check {
        flex: 0 0 auto;
        font-weight: 700;
      }

      .stratus-upsell__frequency {
        margin: -2px 0 20px;
        font-size: 1.2rem;
        font-weight: 600;
      }

      .stratus-upsell__accept {
        min-height: 52px;
      }

      .stratus-upsell__accept[disabled] {
        cursor: wait;
        opacity: 0.7;
      }

      .stratus-upsell__decline {
        display: block;
        width: 100%;
        margin: 16px auto 0;
        padding: 4px;
        color: inherit;
        font: inherit;
        font-size: 1.2rem;
        text-align: center;
        text-decoration: underline;
        text-underline-offset: 3px;
        background: transparent;
        border: 0;
        cursor: pointer;
        opacity: 0.72;
      }

      .stratus-upsell__decline:hover {
        opacity: 1;
      }

      .stratus-upsell__error {
        margin-bottom: 16px;
      }

      body.stratus-upsell-open {
        overflow: hidden;
      }

      @keyframes stratus-upsell-fade-in {
        from {
          opacity: 0;
        }

        to {
          opacity: 1;
        }
      }

      @keyframes stratus-upsell-slide-in {
        from {
          opacity: 0;
          transform: translateY(14px) scale(0.985);
        }

        to {
          opacity: 1;
          transform: translateY(0) scale(1);
        }
      }

      @media (max-width: 600px) {
        .stratus-upsell {
          align-items: flex-end;
          padding: 0;
        }

        .stratus-upsell__dialog {
          width: 100%;
          max-height: calc(100vh - 16px);
          padding: 34px 22px 24px;
          border-radius: 14px 14px 0 0;
        }
      }

      @media (prefers-reduced-motion: reduce) {
        .stratus-upsell__overlay,
        .stratus-upsell__dialog {
          animation: none;
        }
      }
    `;

    document.head.appendChild(style);
  }

  /* =========================================================
     QUALIFICATION
     ========================================================= */

  function getProductFormData(form) {
    const formData = new FormData(form);

    return {
      variantId: String(formData.get('id') || ''),
      sellingPlan: String(formData.get('selling_plan') || ''),
      quantity: Math.max(
        1,
        parseInt(formData.get('quantity') || '1', 10) || 1
      )
    };
  }

  function isQualifyingVariant(variantId) {
    return variantId === CONFIG.variants.cleanser
      || variantId === CONFIG.variants.moisturizer;
  }

  function getMatchingBundlePlan(singleSellingPlan) {
    if (!singleSellingPlan) return '';

    const plan = CONFIG.sellingPlans.singles[singleSellingPlan];

    return plan ? plan.bundlePlan : null;
  }

  async function bundleIsAlreadyInCart() {
    try {
      const response = await fetch(`${getShopifyRoot()}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });

      if (!response.ok) return false;

      const cart = await response.json();

      return cart.items.some(
        (item) => String(item.variant_id) === CONFIG.variants.completeSystem
      );
    } catch (error) {
      console.warn('Stratus upsell could not inspect the cart.', error);
      return false;
    }
  }

function passesSynchronousUpsellChecks(productData) {
  if (!isQualifyingVariant(productData.variantId)) return false;
  if (hasAcceptedOffer()) return false;
  if (hasShownThisSession()) return false;
  if (isInDeclineCooldown()) return false;

  /*
   * Do not interrupt an unknown Joy selling plan.
   */
  if (
    productData.sellingPlan
    && getMatchingBundlePlan(productData.sellingPlan) === null
  ) {
    return false;
  }

  return true;
}

  async function shouldShowUpsell(productData) {
  if (!passesSynchronousUpsellChecks(productData)) {
    return false;
  }

  return !(await bundleIsAlreadyInCart());
}

  /* =========================================================
     FORM INTERCEPTION
     ========================================================= */

  async function handleProductFormSubmit(event) {
  const form = event.target;

  if (!(form instanceof HTMLFormElement)) return;

  if (!form.matches('form.js-product-form[action*="/cart/add"]')) {
    return;
  }

  if (bypassedForms.has(form)) {
    bypassedForms.delete(form);
    return;
  }

  const productData = getProductFormData(form);

  /*
   * Let Alchemy handle the submission normally when the popup
   * is already disqualified for a synchronous reason.
   */
  if (!passesSynchronousUpsellChecks(productData)) {
    return;
  }

  /*
   * We only intercept now because we still need to inspect the cart.
   */
  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  const submitter = event.submitter || null;
  const showOffer = await shouldShowUpsell(productData);

  if (!showOffer) {
    continueOriginalSubmission(form, submitter);
    return;
  }

  pendingForm = form;
  pendingSubmitter = submitter;
  pendingQuantity = productData.quantity;
  pendingSellingPlan = productData.sellingPlan;

  markShownThisSession();
  openModal(productData);
}

  /* =========================================================
     MODAL BEHAVIOR
     ========================================================= */

  function openModal(productData) {
    createModal();

    const modal = document.getElementById('stratus-bundle-upsell');
    const dialog = modal.querySelector('.stratus-upsell__dialog');
    const description = modal.querySelector('.stratus-upsell__description');
    const frequency = modal.querySelector('.stratus-upsell__frequency');
    const acceptButton = modal.querySelector('.stratus-upsell__accept');
    const error = modal.querySelector('.stratus-upsell__error');

    const planData = productData.sellingPlan
      ? CONFIG.sellingPlans.singles[productData.sellingPlan]
      : null;

    error.hidden = true;
    error.textContent = '';

    if (planData) {
      description.textContent = CONFIG.copy.subscriptionBody;
      frequency.textContent =
        `Your Complete System will be delivered ${planData.frequency.toLowerCase()}.`;
      frequency.hidden = false;
      acceptButton.textContent = CONFIG.copy.subscriptionButton;
    } else {
      description.textContent = CONFIG.copy.oneTimeBody;
      frequency.textContent = '';
      frequency.hidden = true;
      acceptButton.textContent = CONFIG.copy.oneTimeButton;
    }

    acceptButton.disabled = false;

    previouslyFocusedElement = document.activeElement;

    modal.hidden = false;
    document.body.classList.add('stratus-upsell-open');

    requestAnimationFrame(() => {
      dialog.focus();
    });
  }

  function closeModal() {
    const modal = document.getElementById('stratus-bundle-upsell');

    if (!modal) return;

    modal.hidden = true;
    document.body.classList.remove('stratus-upsell-open');

    if (
      previouslyFocusedElement
      && typeof previouslyFocusedElement.focus === 'function'
    ) {
      previouslyFocusedElement.focus();
    }

    previouslyFocusedElement = null;
  }

  function handleModalKeydown(event) {
    const modal = document.getElementById('stratus-bundle-upsell');

    if (!modal || modal.hidden) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      declineUpsell();
      return;
    }

    if (event.key !== 'Tab') return;

    const focusableElements = Array.from(
      modal.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), ' +
        'select:not([disabled]), textarea:not([disabled]), ' +
        '[tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => !element.hidden);

    if (!focusableElements.length) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (event.shiftKey && document.activeElement === firstElement) {
      event.preventDefault();
      lastElement.focus();
    } else if (
      !event.shiftKey
      && document.activeElement === lastElement
    ) {
      event.preventDefault();
      firstElement.focus();
    }
  }

  function declineUpsell(event) {
    if (event) event.preventDefault();
    if (isProcessing) return;

    const form = pendingForm;
    const submitter = pendingSubmitter;

    startDeclineCooldown();
    closeModal();
    resetPendingState();

    if (form) {
      continueOriginalSubmission(form, submitter);
    }
  }

  /* =========================================================
     ACCEPTING THE UPSELL
     ========================================================= */

  async function acceptUpsell() {
    if (isProcessing) return;

    const modal = document.getElementById('stratus-bundle-upsell');
    const acceptButton = modal.querySelector('.stratus-upsell__accept');
    const errorBox = modal.querySelector('.stratus-upsell__error');

    isProcessing = true;
    acceptButton.disabled = true;
    acceptButton.textContent = CONFIG.copy.loadingButton;
    errorBox.hidden = true;
    errorBox.textContent = '';

    try {
      const matchingBundlePlan =
        getMatchingBundlePlan(pendingSellingPlan);

      if (pendingSellingPlan && !matchingBundlePlan) {
        throw new Error(
          'The matching Complete System subscription option could not be found.'
        );
      }

      const subscriptionDetails = pendingSellingPlan
  ? CONFIG.sellingPlans.singles[pendingSellingPlan]
  : null;

const lineItem = {
  id: Number(CONFIG.variants.completeSystem),
  quantity: pendingQuantity,
  properties: {
    Contents: CONFIG.bundleContents,

    // Private tracking properties
    _stratus_upsell: 'true',
    _upsell_offer: 'Complete System popup',
    _upsell_purchase_type: pendingSellingPlan
      ? 'Subscription'
      : 'Buy once',
    _upsell_frequency: subscriptionDetails
      ? subscriptionDetails.frequency
      : 'Not applicable'
  }
};

      if (matchingBundlePlan) {
        lineItem.selling_plan = Number(matchingBundlePlan);
      }

      const cartDrawer = document.getElementById('cart-drawer');

      const sections = cartDrawer
        && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer
          .getSectionsToRender()
          .map((section) => section.section)
        : [];

      const requestBody = {
        items: [lineItem],
        sections_url: window.location.pathname
      };

      if (sections.length) {
        requestBody.sections = sections;
      }

      const response = await fetch(`${getShopifyRoot()}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description
          || data.message
          || 'The Complete System could not be added.'
        );
      }

      markOfferAccepted();
      closeModal();
      resetPendingState();

      await updateAndOpenAlchemyDrawer(data);
    } catch (error) {
      console.error('Stratus bundle upsell error:', error);

      errorBox.textContent =
        error.message
        || 'Something went wrong. Please try again.';

      errorBox.hidden = false;
      acceptButton.disabled = false;

      acceptButton.textContent = pendingSellingPlan
        ? CONFIG.copy.subscriptionButton
        : CONFIG.copy.oneTimeButton;
    } finally {
      isProcessing = false;
    }
  }

  /* =========================================================
     ALCHEMY CART DRAWER INTEGRATION
     ========================================================= */

  async function updateAndOpenAlchemyDrawer(data) {
    const cartDrawer = document.getElementById('cart-drawer');

    if (
      cartDrawer
      && data.sections
      && typeof cartDrawer.renderContents === 'function'
    ) {
      await cartDrawer.renderContents(data, true);
      return;
    }

    /*
     * Fallback using Alchemy's documented custom events.
     */
    document.dispatchEvent(
      new CustomEvent('dispatch:cart-drawer:refresh', {
        bubbles: true,
        cancelable: false
      })
    );

    window.setTimeout(() => {
      document.dispatchEvent(
        new CustomEvent('dispatch:cart-drawer:open', {
          bubbles: true,
          cancelable: false
        })
      );
    }, 250);
  }

  /* =========================================================
     ORIGINAL FORM SUBMISSION
     ========================================================= */

  function continueOriginalSubmission(form, submitter) {
  bypassedForms.add(form);

  /*
   * Restart on the next frame so Alchemy can finish handling
   * the intercepted click before receiving the new submission.
   * This delay is effectively imperceptible.
   */
  window.requestAnimationFrame(() => {
    if (
      submitter
      && submitter.form === form
      && !submitter.disabled
      && typeof form.requestSubmit === 'function'
    ) {
      form.requestSubmit(submitter);
      return;
    }

    if (typeof form.requestSubmit === 'function') {
      form.requestSubmit();
      return;
    }

    form.submit();
  });
}

  /* =========================================================
     GENERAL HELPERS
     ========================================================= */

  function resetPendingState() {
    pendingForm = null;
    pendingSubmitter = null;
    pendingQuantity = 1;
    pendingSellingPlan = '';
  }

  function getShopifyRoot() {
    return window.Shopify
      && window.Shopify.routes
      && window.Shopify.routes.root
      ? window.Shopify.routes.root
      : '/';
  }

  /* =========================================================
     INITIALIZATION
     ========================================================= */

  createModal();

  /*
   * Capture phase is intentional. It allows this script to intercept
   * the form before Alchemy's native product-form listener receives it.
   */
  document.addEventListener(
    'submit',
    handleProductFormSubmit,
    true
  );
})();